import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Design system §08. White surface, 1px border, 14px radius, no shadow at rest.
 * Lift on hover only when the whole card is clickable (`interactive`).
 */
export interface CardProps extends HTMLAttributes<HTMLElement> {
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Hover lift + pointer. Use only when the entire card activates one action. */
  interactive?: boolean;
  /** Muted paper surface — locked or unavailable content. */
  muted?: boolean;
  /** Dashed border — "nothing here yet" placeholder card. */
  dashed?: boolean;
  /** Dark panel (band hero, emphasised pricing). */
  inverted?: boolean;
  as?: 'div' | 'article' | 'section' | 'li';
  /** @deprecated pre-redesign prop — kept so unmigrated pages compile. */
  elevated?: boolean;
  /** @deprecated pre-redesign prop — kept so unmigrated pages compile. */
  accentRail?: boolean;
  children?: ReactNode;
}

const PADDING = {
  none: '',
  sm: 'p-4',
  md: 'p-5',
  lg: 'p-6',
} as const;

export function Card({
  padding = 'md',
  interactive = false,
  muted = false,
  dashed = false,
  inverted = false,
  as = 'div',
  elevated = false,
  accentRail = false,
  className,
  children,
  ...rest
}: CardProps) {
  const Tag = as;

  return (
    <Tag
      className={cn(
        'rounded-card border',
        elevated && 'shadow-e1',
        accentRail && 'border-l-4 border-l-variant',
        inverted
          ? 'border-ink-panel-border bg-ink-panel text-ink-panel-text'
          : muted
            ? 'border-border bg-paper'
            : 'border-border bg-surface',
        dashed && 'border-dashed border-border-strong',
        interactive &&
          'cursor-pointer transition-shadow duration-150 hover:shadow-e1 focus-within:shadow-e1',
        PADDING[padding],
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/** Divider inside a card — used above card footers. */
export function CardDivider({ className }: { className?: string }) {
  return <div className={cn('border-t border-border', className)} />;
}

/** Small uppercase section label (design system "overline"). */
export function Overline({
  children,
  className,
  as = 'h3',
}: {
  children: ReactNode;
  className?: string;
  as?: 'h2' | 'h3' | 'h4' | 'p' | 'div' | 'span';
}) {
  const Tag = as;
  return <Tag className={cn('eyebrow text-muted', className)}>{children}</Tag>;
}
